/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/pricelist-fast-add.ts
 * FILE_ID: QB-AGGRID-FAST-ADD-001
 * PURPOSE: Pure helpers for the quote-grid pricelist fast-add combobox.
 * WHY: Keep search/ranking testable and separate from the React input wiring.
 * FLOW: pricelist rows + sections -> searchable entries -> filtered suggestions.
 * RELATED: ScopePricelistFastAdd.tsx, pricelist-prefill.ts
 */
import { itemToRow, resolvePickedItem } from '@quotech/pricelist'
import type {
  PickedItem,
  PricelistItem,
  PricelistSection,
} from '@quotech/pricelist'
import type { PriceBookItem } from '@/lib/api/pricing-api/types'
import { mapPickedItemToPriceBookItem } from './mapPickedItemToPriceBookItem'

export interface FastAddOption {
  id: string
  code: string
  name: string
  description: string
  unit: string
  sectionName: string
  searchableText: string
  priceBookItem: PriceBookItem
}

const MIN_QUERY_CHARS = 2

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function normalise(value: string): string {
  return value.trim().toLowerCase()
}

function buildSearchText(parts: string[]): string {
  return normalise(parts.filter(Boolean).join(' '))
}

export function buildFastAddOptions(
  items: PricelistItem[],
  sections: PricelistSection[],
): FastAddOption[] {
  const sectionById = new Map(sections.map((s) => [s.id, s]))
  return items
    .filter((item) => item.is_active !== false)
    .map((item) => {
      const section = item.section ? sectionById.get(item.section) : undefined
      const picked: PickedItem = resolvePickedItem(
        itemToRow(item),
        section?.column_schema ?? [],
      )
      const priceBookItem = mapPickedItemToPriceBookItem(picked)
      const code = text(item.item_code || picked.item_code)
      const name = text(item.item_name || picked.item_name)
      const description = text(item.description || picked.description)
      const unit = text(picked.unit || item.unit)
      const sectionName = text(section?.name)
      return {
        id: item.id,
        code,
        name,
        description,
        unit,
        sectionName,
        searchableText: buildSearchText([
          code,
          name,
          description,
          unit,
          sectionName,
        ]),
        priceBookItem,
      }
    })
}

export function filterFastAddOptions(
  options: FastAddOption[],
  query: string,
  limit = 8,
): FastAddOption[] {
  const q = normalise(query)
  if (q.length < MIN_QUERY_CHARS) return []
  const starts: FastAddOption[] = []
  const contains: FastAddOption[] = []
  for (const option of options) {
    const code = normalise(option.code)
    const name = normalise(option.name)
    if (code.startsWith(q) || name.startsWith(q)) {
      starts.push(option)
    } else if (option.searchableText.includes(q)) {
      contains.push(option)
    }
    if (starts.length + contains.length >= limit) break
  }
  return [...starts, ...contains].slice(0, limit)
}
